import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LawyerSidebarNavigation from '../../components/ui/LawyerSidebarNavigation';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ProfilePhotoUpload from './components/ProfilePhotoUpload';
import SpecializationSelect from './components/SpecializationSelect';
import ExperienceInput from './components/ExperienceInput';
import BiographyTextarea from './components/BiographyTextarea';
import ProfileProgressIndicator from './components/ProfileProgressIndicator';
import FormValidationSummary from './components/FormValidationSummary';

const LawyerProfileSetup = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    fullName: '',
    specialization: '',
    experience: 0,
    contactNumber: '',
    email: 'ahmad.mohammadi@example.com', // Pre-filled from authentication
    biography: '',
    profilePhoto: null,
    licenseNumber: '',
    barAssociation: '',
    officeAddress: '',
    consultationFee: ''
  });

  // Validation State
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Progress Tracking
  const [currentStep, setCurrentStep] = useState(1);

  // Required fields for validation
  const requiredFields = ['fullName', 'specialization', 'experience', 'contactNumber'];
  const totalFields = Object.keys(formData)?.length;
  const completedFields = Object.values(formData)?.filter(value => 
    value !== '' && value !== null && value !== 0
  )?.length;

  // Validation Rules
  const validateField = (name, value) => {
    switch (name) {
      case 'fullName':
        if (!value || value?.trim()?.length < 2) {
          return 'نام کامل باید حداقل ۲ کاراکتر باشد';
        }
        break;
      case 'specialization':
        if (!value) {
          return 'انتخاب تخصص الزامی است';
        }
        break;
      case 'experience':
        if (value < 0 || value > 50) {
          return 'سال‌های تجربه باید بین ۰ تا ۵۰ باشد';
        }
        break;
      case 'contactNumber':
        const phoneRegex = /^(\+98|0)?9\d{9}$/;
        if (!value) {
          return 'شماره تماس الزامی است';
        }
        if (!phoneRegex?.test(value?.replace(/\s/g, ''))) {
          return 'شماره تماس معتبر نیست';
        }
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value && !emailRegex?.test(value)) {
          return 'آدرس ایمیل معتبر نیست';
        }
        break;
      case 'consultationFee':
        if (value && (isNaN(value) || value < 0)) {
          return 'مبلغ مشاوره باید عددی مثبت باشد';
        }
        break;
      default:
        break;
    }
    return '';
  };

  // Handle Input Changes
  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation
    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));

    // Mark field as touched
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));
  };

  // Calculate current step based on completed fields
  useEffect(() => {
    const basicFields = ['fullName', 'specialization'];
    const experienceFields = ['experience'];
    const contactFields = ['contactNumber', 'email'];
    
    const basicCompleted = basicFields?.every(field => formData?.[field]);
    const experienceCompleted = experienceFields?.every(field => formData?.[field] !== 0);
    const contactCompleted = contactFields?.every(field => formData?.[field]);

    if (contactCompleted) {
      setCurrentStep(4);
    } else if (experienceCompleted) {
      setCurrentStep(3);
    } else if (basicCompleted) {
      setCurrentStep(2);
    } else {
      setCurrentStep(1);
    }
  }, [formData]);

  // Form Submission
  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Validate all required fields
    const newErrors = {};
    requiredFields?.forEach(field => {
      const error = validateField(field, formData?.[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Show success modal
      setShowSuccessModal(true);
      
      // Navigate to dashboard after delay
      setTimeout(() => {
        navigate('/lawyer-dashboard');
      }, 3000);
      
    } catch (error) {
      console.error('Profile setup failed:', error);
      setErrors({ submit: 'خطا در ذخیره اطلاعات. لطفاً دوباره تلاش کنید.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Check if form is valid
  const isFormValid = requiredFields?.every(field => 
    formData?.[field] && !errors?.[field]
  ) && Object.keys(errors)?.length === 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar Navigation */}
      <LawyerSidebarNavigation 
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      {/* Main Content */}
      <div className={`transition-all duration-300 ${
        sidebarCollapsed ? 'lg:mr-16' : 'lg:mr-64'
      } pb-20 lg:pb-8`}>
        <div className="max-w-4xl mx-auto p-4 lg:p-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center mb-4">
              <Icon name="User" size={24} className="text-primary ml-3" />
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                تکمیل پروفایل حرفه‌ای
              </h1>
            </div>
            <p className="text-muted-foreground">
              اطلاعات حرفه‌ای خود را تکمیل کنید تا بتوانید مشاوره ارائه دهید
            </p>
          </div>

          {/* Progress Indicator */}
          <ProfileProgressIndicator 
            currentStep={currentStep}
            totalSteps={4}
          />

          {/* Validation Summary */}
          <FormValidationSummary 
            errors={errors}
            completedFields={completedFields}
            totalFields={totalFields}
          />

          {/* Profile Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Profile Photo Section */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-card-foreground mb-4">
                تصویر پروفایل
              </h2>
              <ProfilePhotoUpload
                value={formData?.profilePhoto}
                onChange={(value) => handleInputChange('profilePhoto', value)}
                error={errors?.profilePhoto}
              />
            </div>

            {/* Basic Information */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-card-foreground mb-6">
                اطلاعات پایه
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  label="نام کامل"
                  type="text"
                  placeholder="نام و نام خانوادگی خود را وارد کنید"
                  value={formData?.fullName}
                  onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                  error={errors?.fullName}
                  required
                />

                <SpecializationSelect
                  value={formData?.specialization}
                  onChange={(value) => handleInputChange('specialization', value)}
                  error={errors?.specialization}
                  required
                />
              </div>
            </div>

            {/* Professional Experience */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-card-foreground mb-6">
                تجربه حرفه‌ای
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <ExperienceInput
                  value={formData?.experience}
                  onChange={(value) => handleInputChange('experience', value)}
                  error={errors?.experience}
                  required
                />

                <Input
                  label="شماره پروانه وکالت"
                  type="text"
                  placeholder="شماره پروانه کانون وکلا"
                  value={formData?.licenseNumber}
                  onChange={(e) => handleInputChange('licenseNumber', e?.target?.value)}
                  error={errors?.licenseNumber}
                />
              </div>

              <div className="mt-6">
                <Input
                  label="کانون وکلای مربوطه"
                  type="text"
                  placeholder="نام کانون وکلای استان"
                  value={formData?.barAssociation}
                  onChange={(e) => handleInputChange('barAssociation', e?.target?.value)}
                  error={errors?.barAssociation}
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-card-foreground mb-6">
                اطلاعات تماس
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <Input
                  label="شماره تماس"
                  type="tel"
                  placeholder="09123456789"
                  value={formData?.contactNumber}
                  onChange={(e) => handleInputChange('contactNumber', e?.target?.value)}
                  error={errors?.contactNumber}
                  required
                />

                <Input
                  label="آدرس ایمیل"
                  type="email"
                  placeholder="example@email.com"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  error={errors?.email}
                  disabled
                />
              </div>

              <div className="mt-6">
                <Input
                  label="آدرس دفتر کار"
                  type="text"
                  placeholder="آدرس کامل دفتر وکالت"
                  value={formData?.officeAddress}
                  onChange={(e) => handleInputChange('officeAddress', e?.target?.value)}
                  error={errors?.officeAddress}
                />
              </div>
            </div>

            {/* Professional Details */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-card-foreground mb-6">
                جزئیات حرفه‌ای
              </h2>
              
              <div className="space-y-6">
                <BiographyTextarea
                  value={formData?.biography}
                  onChange={(value) => handleInputChange('biography', value)}
                  error={errors?.biography}
                  maxLength={1000}
                />

                <Input
                  label="هزینه مشاوره (تومان)"
                  type="number"
                  placeholder="مبلغ هزینه مشاوره به تومان"
                  value={formData?.consultationFee}
                  onChange={(e) => handleInputChange('consultationFee', e?.target?.value)}
                  error={errors?.consultationFee}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <Button
                type="submit"
                variant="default"
                size="lg"
                loading={isLoading}
                disabled={!isFormValid}
                iconName="Save"
                iconPosition="right"
                className="flex-1 sm:flex-none"
              >
                {isLoading ? 'در حال ذخیره...' : 'تکمیل پروفایل'}
              </Button>

              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => navigate('/lawyer-dashboard')}
                iconName="ArrowRight"
                iconPosition="right"
              >
                ذخیره و ادامه بعداً
              </Button>
            </div>

            {/* Form Error */}
            {errors?.submit && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <div className="flex items-center">
                  <Icon name="AlertCircle" size={20} className="text-destructive ml-3" />
                  <p className="text-sm text-destructive">{errors?.submit}</p>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[300] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-moderate max-w-md w-full p-6 text-center">
            <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Check" size={32} color="white" />
            </div>
            <h3 className="text-lg font-semibold text-card-foreground mb-2">
              پروفایل با موفقیت تکمیل شد
            </h3>
            <p className="text-muted-foreground mb-4">
              اطلاعات شما ذخیره شد و به زودی به داشبورد منتقل خواهید شد
            </p>
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="mr-3 text-sm text-muted-foreground">
                در حال انتقال...
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LawyerProfileSetup;